/**
 * 学年・クラス・出席番号の表記ゆれ対策。
 *
 * 保存側: 数字だけで入力された学年・クラスに「年」「組」を自動で付ける。
 *   '1' → '1年' / '2' → '2組'。'A組' や '建築学分野' はそのまま。
 * 読み出し側: 数字を含む値は数値順、数字のない値はその後ろで日本語順に並べる。
 *   database/111_add_user_roster_sort_columns.sql の grade_sort / class_sort /
 *   attendance_sort による DB 側の並びと同じ規則にしてある。
 */

/** 全角数字を半角に寄せる（'１年' → '1年'） */
export const toHalfWidthDigits = (value: string): string =>
  value.replace(/[０-９]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xfee0))

/** trim + 全角数字の半角化。CSV（CRLF）の末尾 \r もここで落ちる */
const cleanValue = (value?: string | null): string =>
  value ? toHalfWidthDigits(value.trim()) : ''

const appendUnitIfDigitsOnly = (value: string, unit: string): string =>
  /^\d+$/.test(value) ? `${value}${unit}` : value

/** '1' → '1年'。'1年' や '校長' はそのまま */
export const normalizeGrade = (value?: string | null): string =>
  appendUnitIfDigitsOnly(cleanValue(value), '年')

/** '2' → '2組'。'A組' や '建築学分野' はそのまま */
export const normalizeClassName = (value?: string | null): string =>
  appendUnitIfDigitsOnly(cleanValue(value), '組')

/** '１５' → '15'。単位は付けない */
export const normalizeAttendanceNumber = (value?: string | null): string => cleanValue(value)

/** 値に含まれる最初の数字列（なければ null） */
const leadingNumber = (value?: string | null): number | null => {
  const matched = cleanValue(value).match(/\d+/)
  return matched ? Number(matched[0]) : null
}

/** 数字を含む値を数値順で先に、数字のない値は後ろで日本語順に */
export const compareRosterValue = (a?: string | null, b?: string | null): number => {
  const numA = leadingNumber(a)
  const numB = leadingNumber(b)

  if (numA !== null && numB !== null && numA !== numB) return numA - numB
  if (numA !== null && numB === null) return -1
  if (numA === null && numB !== null) return 1

  return (a || '').localeCompare(b || '', 'ja')
}

interface RosterFields {
  grade?: string | null
  class?: string | null
  attendance_number?: string | null
  name?: string | null
}

/** 名簿順（学年 → クラス → 出席番号 → 名前）の比較関数 */
export const compareStudentsForRoster = (a: RosterFields, b: RosterFields): number =>
  compareRosterValue(a.grade, b.grade) ||
  compareRosterValue(a.class, b.class) ||
  compareRosterValue(a.attendance_number, b.attendance_number) ||
  (a.name || '').localeCompare(b.name || '', 'ja')
